import cn from 'classnames';
import _ from 'lodash';
import { MUIDataTableColumn, MUIDataTableMeta, MUIDataTableOptions } from 'mui-datatables';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { MODAL_TYPES } from 'src/appConfig/modal';
import { Button, IconClickable, Table, View } from 'src/components/common';
import { useComponentWillUnmount } from 'src/hooks';
import modalActions from 'src/redux/modal/actions';
import {
  deletePhaseAsync,
  getPhasesAsync,
  setPhaseModalType,
  setRefreshTable,
  setSelectedPhase,
} from 'src/redux/phasecodes/actions';
import { INITIAL_PHASE } from 'src/redux/phasecodes/reducer';
import { GetPhasesParams, PhaseModalType, PhaseModalTypes } from 'src/redux/phasecodes/types';
import { IRootState } from 'src/redux/rootReducer';
import { LockupValueByLabel } from 'src/utils';
import { isEmpty } from 'src/validations';
import PhasesModal from './PhasesModal';
import './styles.scss';

const PhaseCodesContainer: React.FC<Props> = ({
  loading,
  phases,
  totalPhases,
  onGetPhases,
  onSetOpenModal,
  onSetSelectedPhase,
  status,
  isRefresh,
  onSetRefreshTable,
  onShowConfirmModal,
  onCloseConfirmModal,
  onDeletePhase,
}) => {
  const [refresh, doRefresh] = useState<number>(0);
  useComponentWillUnmount(() => onSetOpenModal(null as PhaseModalType));

  useEffect(() => {
    if (isRefresh) {
      doRefreshTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefresh]);

  useComponentWillUnmount(() => onSetOpenModal(null as PhaseModalType));

  const doRefreshTable = () => {
    doRefresh(prev => prev + 1);
    onSetRefreshTable(false);
  };

  const filterParamsPhase = (params: GetPhasesParams) => {
    let res = params;
    Object.keys(res).forEach(key => {
      if (isEmpty(res[key])) delete res[key];
      if (key === 'status') {
        res['status'] = LockupValueByLabel(params['status'][0] as string, status);
        // delete res[key];
      }
    });
    return res;
  };

  const handleGetEmployees = (params: GetPhasesParams) => {
    // console.log(filterParamsPhase(params));
    onGetPhases(filterParamsPhase(params));
  };

  const handleCloseModal = () => {
    onSetSelectedPhase(INITIAL_PHASE);
    onSetOpenModal(false as PhaseModalType);
  };

  const handleEditRow = meta => {
    const index = meta.rowIndex;
    onSetSelectedPhase(phases[index]);
    onSetOpenModal(PhaseModalTypes.EDIT as PhaseModalType);
  };

  const onDeleteRow = meta => {
    const jobName = meta.rowData[0] || '';

    onShowConfirmModal({
      type: MODAL_TYPES.yesNoModal,
      data: {
        title: 'Delete Phase',
        message: `Are you sure that you want to delete this phase? \n "${jobName}"`,
        onOk: () => handleDeleteRow(meta),
        onCancel: onCloseConfirmModal,
      },
    });
  };

  const handleDeleteRow = meta => {
    const phase = phases[meta.rowIndex];

    onCloseConfirmModal();
    onDeletePhase({ id: phase.id || 0 });
  };

  const tableColumns: MUIDataTableColumn[] = [
    { name: 'code', label: 'Code', options: { filter: false, sort: true } },
    { name: 'description', label: 'Description', options: { filter: false, sort: false } },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        filterOptions: {
          names: status.map(item => item.label),
        },
        customBodyRender: (value: string) => (
          <span className={cn({ 'active-status': value === 'active' }, { 'inactive-status': value === 'inactive' })}>
            {_.capitalize(value)}
          </span>
        ),
      },
    },
    {
      name: '',
      label: 'Action',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_value: any, meta: MUIDataTableMeta) => (
          <>
            <IconClickable
              className={cn('text-is-18', 'ml-2', 'ctn-job__action')}
              name="ic_edit"
              onClick={() => handleEditRow(meta)}
            />
            <IconClickable
              className={cn('text-is-18', 'ml-2', 'ctn-job__action')}
              name="ic_trash"
              onClick={() => onDeleteRow(meta)}
            />
          </>
        ),
      },
    },
  ];

  const tableOptions: MUIDataTableOptions = {
    count: totalPhases,
    searchPlaceholder: 'Search by code or description',
  };

  return (
    <View className="ctn-employees mt-32">
      <View className="ctn-employees__header mb-32">
        <View>
          <h3>Manage Phase Codes</h3>
        </View>
        <View>
          <Button onClick={() => onSetOpenModal(PhaseModalTypes.ADD as PhaseModalType)}>ADD NEW PHASE CODE</Button>
        </View>
      </View>

      <Table
        title=""
        data={phases}
        tableOptions={tableOptions}
        isLoading={loading}
        // defaultSortOrder={{ name: 'id', direction: 'desc' }}
        refresh={refresh}
        columns={tableColumns}
        onAction={handleGetEmployees}
      />
      <PhasesModal onClose={handleCloseModal} />
    </View>
  );
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const mapStateToProps = (state: IRootState) => ({
  loading: state.phaseCodes.isLoading,
  phases: state.phaseCodes.phases,
  totalPhases: state.phaseCodes.totalPhases,
  status: state.content.status,
  isRefresh: state.phaseCodes.isRefresh,
});

const mapDispatchToProps = {
  onGetPhases: getPhasesAsync.request,
  onSetOpenModal: setPhaseModalType,
  onSetSelectedPhase: setSelectedPhase,
  onDeletePhase: deletePhaseAsync.request,
  onShowConfirmModal: modalActions.showModal,
  onCloseConfirmModal: modalActions.closeModal,
  onSetRefreshTable: setRefreshTable,
};

export default connect(mapStateToProps, mapDispatchToProps)(PhaseCodesContainer);
